import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ChevronsUpDown } from "lucide-react";
import { fromJS } from "immutable";
import NumericFormatFieldDebounce from "../../components/ui/NumericFormatFieldDebounce";
import InputFieldDebounce from "../../components/ui/InputFieldDebounce";
import { SelectField } from "../../components/ui";
import LabelsSelect from "../../components/LabelsSelect";
import CustomField from "../CustomField";
import DatePicker from "../../board/components/TaskDetail/DatePicker";
import AssigneesSelect from "../../components/AssigneesSelect";
import ItemSelect from "../../components/ItemSelect";
import JiraIssueSelect from "../../components/JiraIssueSelect";
import BucketSelect from "../../components/BucketSelect";
import ProductsPicker from "../component/ProductsPicker";
import { isGmailContext } from "../../api/api";

const SaleForm = ({
  projectId,
  visibleFieldIds,
  isEditMode: _isEditMode,
  taskData,
  onDataChange,
  validationErrors,
}) => {
  const { t } = useTranslation();

  const tenantInfo = useSelector((state: any) => state.tenant.get("tenantInfo"));
  const isJira = tenantInfo.getIn(["host", "product"]) === "JIRA";
  const projectCurrency = useSelector((state: any) =>
    state.projectReducer.getIn(["project", "currency"])
  );
  const currency = projectCurrency || "USD";

  const fields =
    useSelector((state: any) => state.projectReducer.getIn(["project", "fields"])) || fromJS([]);
  const saleStatus = useSelector((state: any) => state.ruleReducer.getIn(["status"])).toJS();
  const saleSources = useSelector((state: any) => state.ruleReducer.getIn(["source"])).toJS();
  if (isGmailContext()) {
    saleSources.unshift({ value: "Chrome extension", label: "Chrome extension" });
  }
  const saleStages = useSelector((state: any) => state.ruleReducer.getIn(["stage"])).toJS();

  const getTaskFieldValue = (fieldId: string) => {
    return taskData.getIn([fieldId, "value"]);
  };

  const productsImmutable = getTaskFieldValue("products");
  const productsPlain: any[] = useMemo(
    () => (productsImmutable?.toJS ? productsImmutable.toJS() : productsImmutable) || [],
    [productsImmutable]
  );

  const customFields = fields.filter(
    (field: any) => field.get("isCustomField") && field.get("formType") == "SALE"
  );

  const handleChangeField = (fieldId, fieldValue) => {
    onDataChange(fieldId, fieldValue);
  };

  const BUILD_FIELDS: Record<string, any> = {
    name: (
      <div>
        <InputFieldDebounce
          required
          testId="sale-form-name-input"
          name="name"
          label={t("Deal Name")}
          placeholder={t("Set deal name...")}
          value={getTaskFieldValue("name")}
          onChange={(value) => handleChangeField("name", value)}
          errorMessage={validationErrors.name}
        />
      </div>
    ),
    bucketId: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">
          {t("Sale Funnels")}
        </label>
        <BucketSelect
          projectId={projectId}
          contentType="SALE"
          type="FORM"
          value={getTaskFieldValue("bucketId")}
          onChange={(bucketId: string) => handleChangeField("bucketId", bucketId)}
          placeholder={t("Select...")}
          isClearable
        />
        {validationErrors.bucketId && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.bucketId}</p>
        )}
      </div>
    ),
    assignees: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Assignees")}</label>
        <AssigneesSelect
          type="FORM"
          value={getTaskFieldValue("assignees")?.toJS() || []}
          onChange={(assigneeIds: string[]) => handleChangeField("assignees", assigneeIds)}
          placeholder={t("Select assignees")}
        />
      </div>
    ),
    labels: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Labels")}</label>
        <LabelsSelect
          projectId={projectId}
          contentType="SALE"
          type="FORM"
          value={getTaskFieldValue("labels")?.toJS() || []}
          onChange={(labelIds: string[]) => handleChangeField("labels", labelIds)}
          placeholder={t("Select labels")}
          showRemove
        />
      </div>
    ),
    contact: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">
          {t("Primary contact")}
        </label>
        <ItemSelect
          testId="sale-form-contact-input"
          projectId={projectId}
          contentType="CONTACT"
          type="FORM"
          value={getTaskFieldValue("contact")?.toJS()}
          onChange={(contact: any) => handleChangeField("contact", contact)}
          placeholder={t("Select contact")}
          isClearable
        />
      </div>
    ),
    account: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">
          {t("Primary account")}
        </label>
        <ItemSelect
          testId="sale-form-company-input"
          projectId={projectId}
          contentType="COMPANY"
          type="FORM"
          value={getTaskFieldValue("account")?.toJS()}
          onChange={(account: any) => handleChangeField("account", account)}
          placeholder={t("Select account")}
          isClearable
        />
      </div>
    ),
    issue: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Jira Issue")}</label>
        <JiraIssueSelect
          type="FORM"
          value={getTaskFieldValue("issue")?.toJS()}
          onChange={(issue: any) => handleChangeField("issue", issue)}
          placeholder={t("Select issue")}
          isClearable
        />
      </div>
    ),
    estimatedRevenue: (
      <div>
        <NumericFormatFieldDebounce
          testId="sale-form-estimated-revenue-input"
          label={t("Estimated Revenue")}
          value={getTaskFieldValue("estimatedRevenue")}
          onChange={(value: string) => {
            handleChangeField("estimatedRevenue", value);
          }}
          placeholder={t("Set a value")}
          errorMessage={validationErrors.estimatedRevenue}
          currency={currency}
          disabled={productsPlain.length > 0}
        />
        <div className="w-full mt-1 flex items-center justify-end">
          <ProductsPicker
            projectId={projectId}
            currency={currency}
            renderTarget={(isOpen, setIsOpen, hasProducts) => {
              return (
                <div
                  className="text-sm text-blue-600 cursor-pointer"
                  onClick={() => setIsOpen(true)}
                >
                  {hasProducts ? t("Edit products") : t("Add products")}
                </div>
              );
            }}
            value={productsPlain}
            onChange={(products, totalAmount) => {
              handleChangeField("estimatedRevenue", totalAmount);
              handleChangeField("products", products);
            }}
          />
        </div>
      </div>
    ),
    estimatedProbability: (
      <div>
        <NumericFormatFieldDebounce
          testId="sale-form-estimated-probability-input"
          label={t("Estimated probability")}
          value={getTaskFieldValue("estimatedProbability")}
          onChange={(value: string) => {
            handleChangeField("estimatedProbability", value);
          }}
          placeholder={t("Set a value")}
          errorMessage={validationErrors.estimatedProbability}
          isPercentage={true}
          min={0}
          max={100}
        />
      </div>
    ),
    status: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Status")}</label>
        <SelectField
          testId="sale-form-status-input"
          menuPlacement="auto"
          options={saleStatus}
          value={saleStatus.find((item) => item.value === getTaskFieldValue("status"))}
          onChange={(selectedOption) => {
            handleChangeField("status", selectedOption?.value);
          }}
          placeholder={t("Select...")}
          isClearable
          dropdownIndicator={<ChevronsUpDown className="w-3.5 h-3.5 text-gray-400" />}
        />
        {validationErrors.status && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.status}</p>
        )}
      </div>
    ),
    finish: (
      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Close Date")}</label>
        <DatePicker
          selected={getTaskFieldValue("finish")}
          onChange={(val: string) => handleChangeField("finish", val)}
        />
      </div>
    ),
    stage: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Stage")}</label>
        <SelectField
          testId="sale-form-stage-input"
          menuPlacement="auto"
          options={saleStages}
          value={saleStages.find((item) => item.value === getTaskFieldValue("stage"))}
          onChange={(selectedOption) => {
            handleChangeField("stage", selectedOption?.value);
          }}
          placeholder={t("Select...")}
          isClearable
          dropdownIndicator={<ChevronsUpDown className="w-3.5 h-3.5 text-gray-400" />}
        />
        {validationErrors.stage && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.stage}</p>
        )}
      </div>
    ),
    source: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Source")}</label>
        <SelectField
          testId="sale-form-source-input"
          menuPlacement="auto"
          options={saleSources}
          value={saleSources.find((item) => item.value === getTaskFieldValue("source"))}
          onChange={(selectedOption) => {
            handleChangeField("source", selectedOption?.value);
          }}
          placeholder={t("Select...")}
          isClearable
          dropdownIndicator={<ChevronsUpDown className="w-3.5 h-3.5 text-gray-400" />}
        />
        {validationErrors.source && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.source}</p>
        )}
      </div>
    ),
  };

  return (
    <div className="space-y-4">
      {visibleFieldIds.map((visibleFieldId) => {
        if (visibleFieldId === "issue" && !isJira) return null;
        const customField = customFields.find((field) => field.get("id") === visibleFieldId);
        if (customField && customField.get("id")) {
          const fieldData = {
            ...customField.toJS(),
            value: getTaskFieldValue(customField.get("id")),
          };
          return (
            <div className="w-full" key={`${customField.get("id")}`}>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                {t(customField.get("label"))}
              </label>
              <CustomField
                field={fieldData}
                onChange={(value: any) => {
                  handleChangeField(customField.get("id"), value);
                }}
              />
            </div>
          );
        }
        return <React.Fragment key={visibleFieldId}>{BUILD_FIELDS[visibleFieldId]}</React.Fragment>;
      })}
    </div>
  );
};

export default SaleForm;
